import { useEffect, useRef, useState } from "react";
import { fitsCentred, followWord, type Span } from "./follow";

/**
 * useAutoScroll keeps the spoken sentence in view, and gets out of the way.
 *
 * Two rules, both from the original build: it is toggleable, and it turns
 * itself off when the reader scrolls away deliberately — fighting somebody who
 * has scrolled back to re-read a paragraph is the worst thing a player does.
 *
 * Telling a deliberate scroll from one we caused ourselves is the whole problem.
 * A flag set around our own scroll call is not enough, because smooth scrolling
 * emits events for hundreds of milliseconds afterwards; we mark a window
 * instead and ignore events inside it. The window is deliberately short — a
 * long one would also swallow a reader who scrolled immediately after a
 * sentence changed, which is exactly when they are most likely to.
 *
 * A sentence that fits is centred once, when it becomes the spoken one. One
 * too tall for that was centred once too, with its first lines above the
 * window and its last below, and nothing moved until the next sentence
 * (requirement 0104): it is followed by its spoken word instead, and only when
 * the word has left the band the eye reads in (follow.ts).
 */
/** SMOOTH_SCROLL_WINDOW_MS covers a smooth scroll's own event storm. */
const SMOOTH_SCROLL_WINDOW_MS = 600;

/**
 * readableArea is the part of the window where text can be read: under a
 * header pinned to the top (the studio's), above the pinned transport.
 */
function readableArea(bar: HTMLElement | null): Span {
  let top = 0;
  const header = document.querySelector("header");
  if (header && ["sticky", "fixed"].includes(getComputedStyle(header).position)) {
    const rect = header.getBoundingClientRect();
    if (rect.top <= 0 && rect.bottom > 0) top = rect.bottom;
  }
  const bottom = bar ? bar.getBoundingClientRect().top : window.innerHeight;
  return { top, bottom };
}

/**
 * activeKey names the spoken sentence and wordKey the spoken word in it; the
 * word only matters inside a sentence too tall to centre.
 */
export function useAutoScroll(
  activeKey: string | number | null,
  wordKey: string | number | null = null,
) {
  const [enabled, setEnabled] = useState(true);
  const containerRef = useRef<HTMLElement | null>(null);
  const activeRef = useRef<HTMLElement | null>(null);
  /** The pinned transport, which covers the bottom of the text. */
  const barRef = useRef<HTMLDivElement | null>(null);
  const ourScrollUntil = useRef(0);
  /** The sentence already placed, so a word moving on does not centre it again. */
  const placed = useRef<string | number | null>(null);

  useEffect(() => {
    const onScroll = () => {
      if (Date.now() < ourScrollUntil.current) return; // our own smooth scroll
      setEnabled(false);
    };
    window.addEventListener("wheel", onScroll, { passive: true });
    window.addEventListener("touchmove", onScroll, { passive: true });
    return () => {
      window.removeEventListener("wheel", onScroll);
      window.removeEventListener("touchmove", onScroll);
    };
  }, []);

  useEffect(() => {
    const sentence = activeRef.current;
    if (!enabled || activeKey == null || !sentence) {
      placed.current = null; // turning Follow back on places the sentence again
      return;
    }
    const changed = placed.current !== activeKey;
    placed.current = activeKey;

    const area = readableArea(barRef.current);
    const box = sentence.getBoundingClientRect();
    if (fitsCentred(box.height, area, window.innerHeight)) {
      if (!changed) return;
      ourScrollUntil.current = Date.now() + SMOOTH_SCROLL_WINDOW_MS;
      sentence.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    // Before its first word is spoken the sentence has no spoken word: its
    // first one stands in, so a tall sentence opens at its start.
    const word =
      sentence.querySelector('[data-testid="word-active"]') ??
      sentence.querySelector("[data-start-ms]");
    if (!word) return;
    const delta = followWord(word.getBoundingClientRect(), area);
    if (delta == null) return;
    ourScrollUntil.current = Date.now() + SMOOTH_SCROLL_WINDOW_MS;
    window.scrollBy({ top: delta, behavior: "smooth" });
  }, [activeKey, wordKey, enabled]);

  return {
    enabled,
    setEnabled: (on: boolean) => {
      if (on) ourScrollUntil.current = Date.now() + SMOOTH_SCROLL_WINDOW_MS;
      setEnabled(on);
    },
    containerRef,
    activeRef,
    barRef,
  };
}
